import React, { useEffect } from "react"
import { useSelector } from "react-redux"
import PropTypes from "prop-types"
import { Button, Row, Col, Typography, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { InfoCircleOutlined } from "@ant-design/icons"
import styles from "./ExperienceBooking.module.css"
import { Text as UiText } from "ui/Typography"

const { Text } = Typography

const GuestsSelector = ({ counts, setCounts, activeTab, error, setBookingErrors, maxGuests, minGuests }) => {
  const {
    experienceBookingData: { bookingSelections }
  } = useSelector((state) => state.experiences)

  const [t] = useTranslation()
  const { children_allowed, infants_allowed, passengers_allowed } = bookingSelections?.tripLength?.price || {}

  useEffect(() => {
    const hasNoGuests = !counts?.main?.adults && !counts?.main?.children && !counts?.main?.infants && !counts?.main?.passengers
    if (minGuests > 0 && hasNoGuests) {
      setCounts({
        main: { adults: minGuests || 0, children: 0, infants: 0, passengers: 0 },
        extra: { adults: 0, children: 0, infants: 0, passengers: 0 }
      })
    }
  }, [minGuests, setCounts])

  const updateCount = (section, key, delta) => {
    const newCounts = {
      ...counts,
      [section]: {
        ...counts?.[section],
        [key]: Math.max(0, (counts?.[section]?.[key] || 0) + delta)
      }
    }
    setCounts(newCounts)

    const total =
      Object.values(newCounts?.main).reduce((sum, v) => sum + v, 0) +
      Object.values(newCounts?.extra).reduce((sum, v) => sum + v, 0)

    if (total > 0 && setBookingErrors) {
      setBookingErrors((prev) => ({ ...prev, counts: false }))
    }
  }

  const getGuestsSummary = () => {
    const labels = { adults: "Adult", children: "Child", infants: "Infant", passengers: "Passenger" }
    const sectionCounts = counts?.main || {}
    return Object.entries(sectionCounts)
      .filter(([, count]) => count > 0)
      .map(([key, count]) => `${count} ${labels[key]}${count > 1 ? "s" : ""}`)
      .join(", ")
  }

  const getTotalGuests = () => {
    const mainCounts = counts?.main || {}
    const extraCounts = counts?.extra || {}
    return (
      Object.values(mainCounts).reduce((sum, v) => sum + (v || 0), 0) +
      Object.values(extraCounts).reduce((sum, v) => sum + (v || 0), 0)
    )
  }

  const clearCounts = () => {
    setCounts({
      main: { adults: 0, children: 0, infants: 0, passengers: 0 },
      extra: { adults: 0, children: 0, infants: 0, passengers: 0 }
    })
  }

  if (activeTab === 2 || activeTab === 3) {
    return (
      <Flex className={styles.inputContainer}>
        <span className={styles.inputLabel}>Guests</span>
        <span className={styles.selectedInputLink} style={{ color: "var(--color-text-secondary-dark)" }}>
          {getGuestsSummary() || "—"}
        </span>
      </Flex>
    )
  }

  const isAtMax = getTotalGuests() >= maxGuests

  const guestRows = [
    { label: "Adults", key: "adults", allowed: true },
    { label: "Children (< 12 years)", key: "children", allowed: children_allowed },
    { label: "Infants (< 2 years)", key: "infants", allowed: infants_allowed },
    {
      label: (
        <>
          Passengers <InfoCircleOutlined style={{ fontSize: 12, color: "#1890ff" }} />
        </>
      ),
      key: "passengers",
      allowed: passengers_allowed
    }
  ].filter(({ allowed }) => allowed)

  return (
    <div className={styles.stepFadeIn}>
      <div className={styles.stepSection}>
        <span className={styles.stepSectionLabel}>
          Guests {minGuests || maxGuests ? `(Min ${minGuests ?? 1}, Max ${maxGuests} allowed)` : ""}
        </span>
        {guestRows.map(({ label, key }) => (
          <Row key={key} justify="space-between" align="middle" style={{ marginBottom: 10 }}>
            <Col>{label}</Col>
            <Col>
              <Button
                size="middle"
                onClick={() => updateCount("main", key, -1)}
                disabled={(counts?.main?.[key] || 0) === 0}
              >
                -
              </Button>
              <Text style={{ margin: "0 12px" }}>{counts?.main?.[key] || 0}</Text>
              <Button
                size="middle"
                onClick={() => updateCount("main", key, 1)}
                disabled={isAtMax}
              >
                +
              </Button>
            </Col>
          </Row>
        ))}
        <Button type="link" style={{ padding: 0 }} onClick={clearCounts}>
          Clear
        </Button>
      </div>
      {error && <UiText color="var(--color-error)" style={{ fontSize: "var(--font-size-s)" }}>{t("webIntegration.embedded_validation.guest")}</UiText>}
    </div>
  )
}

GuestsSelector.propTypes = {
  counts: PropTypes.shape({
    main: PropTypes.object,
    extra: PropTypes.object
  }).isRequired,
  setCounts: PropTypes.func.isRequired,
  activeTab: PropTypes.number,
  error: PropTypes.bool,
  setBookingErrors: PropTypes.func,
  maxGuests: PropTypes.number,
  minGuests: PropTypes.number
}

export default React.memo(GuestsSelector)
